import { toNumber } from 'lodash';
import { toWords } from 'number-to-words';

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  ZAR: 'R',
  PLN: 'zł',
  AUD: '$',
  NOK: 'kr',
  CHF: 'SFr',
};

const TERMS: Record<string, number> = {
  '1d': 1,
  '1w': 7,
  '2w': 14,
  '1m': 30,
  '1m ext': 30,
  '2m': 60,
  '2m ext': 60,
  '3m': 90,
  '3m ext': 90,
  '6m': 180,
  '9m': 270,
  '12m': 365,
};

export const isEmailValid = (email: string) => {
  return email.toLowerCase().match(EMAIL_REGEX);
};

export const getDateString = (separator = '-') => {
  const d = new Date();
  return (
    d.getDate().toString() +
    separator +
    (d.getMonth() + 1).toString() +
    separator +
    d.getFullYear().toString()
  );
};

export async function downloadResponseContent(
  response: Response,
  filename: string
) {
  const blob = await response.blob();
  const newBlob = new Blob([blob]);
  const blobUrl = window.URL.createObjectURL(newBlob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

export async function downloadBase64Data(dataURL: string, filename: string) {
  const link = document.createElement('a');
  link.href = dataURL;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
}

export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] || currency;
}

export function termInDays(term: string): number {
  return TERMS[term];
}

export function calculateMaturityValue(
  amount: number,
  term: string,
  rate: number,
  yieldConvention = 'APY'
): number {
  const termDays = termInDays(term);
  const rateDecimal = rate / 100;
  let yearFraction;
  let maturityPricePerShare;
  switch (yieldConvention) {
    case 'MMY_360':
      yearFraction = termDays / 360;
      maturityPricePerShare = 1 + rateDecimal * yearFraction;
      break;
    case 'MMY_365':
      yearFraction = termDays / 365;
      maturityPricePerShare = 1 + rateDecimal * yearFraction;
      break;
    default: // APY
      yearFraction = termDays / 365;
      maturityPricePerShare = (1 + rateDecimal) ** yearFraction;
      break;
  }
  const value = amount * maturityPricePerShare;
  if (Number.isNaN(value)) {
    return 0;
  }
  return value;
}

export function formatMoney(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const formatYieldConvention = (convention: string) => {
  switch (convention) {
    case 'MMY_360':
      return 'MMY Actual/360';
    case 'MMY_365':
      return 'MMY Actual/365';
    default:
      return convention;
  }
};

function formatFloatString(
  number: number,
  maxInputDecimals: number,
  minDecimals: number,
  thousandSeparator: '' | ',' = ''
): string {
  let [integerPart, decimalPart = ''] = number
    .toFixed(maxInputDecimals)
    .split('.');
  decimalPart = decimalPart.replace(/0+$/, '').padEnd(minDecimals, '0');
  if (thousandSeparator && integerPart.length > 3) {
    integerPart = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      thousandSeparator
    );
  }

  return `${integerPart}.${decimalPart}`;
}

export function formatYieldString(number: number): string {
  return formatFloatString(number, 3, 2, ',');
}

export function verboseTerm(term?: string | null): string {
  if (!term) return '';
  const digitSubsets = term.match(/\d+/);
  const termNum = digitSubsets ? toNumber(digitSubsets[0]) : 0;
  let vTerm = term;
  vTerm = vTerm.replace('m', ' month');
  vTerm = vTerm.replace('w', ' week');
  vTerm = vTerm.replace('y', ' year');
  vTerm = vTerm.replace('d', ' day');
  return termNum > 1 ? `${vTerm}s` : vTerm;
}

export function amountInWords(amount: number): string {
  try {
    return toWords(amount);
  } catch (error) {
    return '';
  }
}
